import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireAdmin = false,
}) => {
  const { user, profile, loading, profileLoading, isAdmin } = useAuth();
  const location = useLocation();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasVerifiedAdmin, setHasVerifiedAdmin] = useState(false);
  const [profileCheckAttempts, setProfileCheckAttempts] = useState(0);

  // Remember the current path for admin pages
  useEffect(() => {
    if (requireAdmin && location.pathname) {
      // Only store admin path for header visibility
      sessionStorage.setItem('showHeader', 'true');
    }
  }, [requireAdmin, location.pathname]);

  // Set a flag after initial authentication check is complete
  useEffect(() => {
    if (!loading) {
      setIsInitialLoad(false);
    }
  }, [loading]);

  // Track when we've successfully verified admin status
  useEffect(() => {
    if (requireAdmin && profile && profile.role === 'admin') {
      setHasVerifiedAdmin(true);
      // Cache admin status to sessionStorage to survive state refreshes
      sessionStorage.setItem('adminVerified', 'true');
    }
  }, [requireAdmin, profile]);

  // Restore cached admin verification on mount
  useEffect(() => {
    const cachedAdminStatus = sessionStorage.getItem('adminVerified');
    if (cachedAdminStatus === 'true' && requireAdmin) {
      setHasVerifiedAdmin(true);
    }
  }, [requireAdmin]);

  // Track profile loading attempts to prevent infinite loading
  useEffect(() => {
    if (profileLoading) {
      setProfileCheckAttempts(prev => prev + 1);
    }
  }, [profileLoading]);

  // Show loading state only during main auth loading, not profile loading
  if (loading || isInitialLoad) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // If authentication is required and user is not logged in
  if (requireAuth && !user) {
    console.log('🚫 ProtectedRoute: Redirecting to login - no user found');
    console.log('📊 Auth state:', { user: !!user, profile: !!profile, loading });
    // Store the current path to redirect back after login
    sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  // For admin pages, show loading during profile fetch - but ONLY if we haven't verified admin before
  // This prevents redirects during temporary profile state loss
  if (requireAdmin && profileLoading && !hasVerifiedAdmin && profileCheckAttempts < 3) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Verifying admin access...</div>
      </div>
    );
  }

  // For admin pages, ONLY redirect if ALL of these conditions are met:
  // 1. Profile loading is complete
  // 2. isAdmin from AuthContext (persisted in secure storage) is false
  // 3. We haven't previously verified admin (cache check)
  // 4. Current profile is loaded and is NOT admin
  // This multi-layered check prevents redirects during transient state changes
  if (requireAdmin && !profileLoading) {
    const cachedAdminStatus = sessionStorage.getItem('adminVerified');

    // Allow access if any of these are true:
    // - We previously verified admin access (cached)
    // - isAdmin from AuthContext is true (persisted)
    // - Current profile shows admin role
    const allowAccess =
      cachedAdminStatus === 'true' ||
      hasVerifiedAdmin ||
      isAdmin ||
      (profile && profile.role === 'admin');

    if (!allowAccess) {
      console.log('🚫 ProtectedRoute: Redirecting non-admin user from admin route');
      console.log('📊 Admin check:', {
        isAdmin,
        hasVerifiedAdmin,
        cachedAdminStatus,
        profile: !!profile,
        profileRole: profile?.role
      });
      // Clear the cache if we're redirecting
      sessionStorage.removeItem('adminVerified');
      return <Navigate to="/" replace />;
    }
  }

  // This check is now handled above with better loading state logic

  // If user is logged in but accessing login page, redirect to dashboard
  if (user && location.pathname === '/login') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
